// ─── What: the header control strip ───────────────────────────────────────────
//
// Presentational. Every piece of state it shows is owned by ConvergenceChart and
// passed down, so this file has no logic beyond which controls are applicable.

import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { ExportButton, type ExportSeries } from '../../ExportButton';
import { formatRange } from '../../chartFormat';
import {
    RESOLUTION_OPTIONS,
    type CandleResolution,
    type EffectiveChartType,
} from '../constants';
import type { OverlayStats } from '../overlays/drawTradeOverlay';

export interface ChartHeaderControlsProps {
    headerExtra?: React.ReactNode;
    displayRange: [Date, Date] | null;
    timeRange: [Date, Date] | null;
    onTimeRangeChange: (range: [Date, Date] | null) => void;

    hasSignalOverlay: boolean;
    showSignals: boolean;
    onToggleSignals: () => void;

    hasWindowTrades: boolean;
    showWindowTrades: boolean;
    onToggleWindowTrades: () => void;
    overlayStats: OverlayStats | null;

    convictionControls?: React.ReactNode;

    effectiveChartType: EffectiveChartType;
    candlesAllowed: boolean;
    stackedAllowed: boolean;
    onChartTypeSelect: (type: EffectiveChartType) => void;

    candleSide: 'yes' | 'no';
    candleSideLabels: { yes: string; no: string };
    onCandleSideSelect: (side: 'yes' | 'no') => void;

    resolution: CandleResolution;
    onResolutionChange: (e: SelectChangeEvent<number>) => void;

    exportSeries: ExportSeries[];
    exportFilenamePrefix: string;

    fullscreen: boolean;
    onToggleFullscreen: () => void;
}

const toggleGroupSx = {
    height: 26,
    '& .MuiToggleButton-root': {
        py: '2px',
        px: 1,
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'none' as const,
        borderColor: 'divider',
    },
};

export function ChartHeaderControls(props: ChartHeaderControlsProps) {
    const theme = useTheme();
    const {
        displayRange,
        timeRange,
        overlayStats,
        showWindowTrades,
        showSignals,
    } = props;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {props.headerExtra}

            {displayRange && (
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        // Emphasised while a window is selected, so the range
                        // reads as a choice rather than a caption.
                        color: timeRange ? 'text.primary' : 'text.secondary',
                        fontWeight: timeRange ? 600 : 400,
                    }}
                >
                    {formatRange(displayRange)}
                </Typography>
            )}

            {timeRange && (
                <Chip
                    label="Reset"
                    size="small"
                    onDelete={() => props.onTimeRangeChange(null)}
                    sx={{
                        height: 22,
                        fontSize: '10px',
                        fontWeight: 600,
                        '& .MuiChip-deleteIcon': { fontSize: '14px' },
                    }}
                />
            )}

            {props.hasSignalOverlay && (
                <Chip
                    label="Signals"
                    size="small"
                    onClick={props.onToggleSignals}
                    variant={showSignals ? 'filled' : 'outlined'}
                    sx={{
                        height: 20,
                        fontSize: '10px',
                        fontFamily: 'monospace',
                        bgcolor: showSignals
                            ? alpha(theme.palette.warning.main, 0.15)
                            : 'transparent',
                        color: showSignals
                            ? theme.palette.warning.main
                            : 'text.secondary',
                        borderColor: showSignals
                            ? theme.palette.warning.main
                            : 'divider',
                        '& .MuiChip-label': { px: 0.75 },
                    }}
                />
            )}

            {props.hasWindowTrades && (
                <Tooltip
                    describeChild
                    title={
                        overlayStats?.clustered
                            ? `${overlayStats.trades.toLocaleString()} trades in view, drawn as ${overlayStats.marks.toLocaleString()} marks`
                            : "Show the window's trades on the plot"
                    }
                >
                    <Chip
                        // The count only appears once clustering has actually
                        // collapsed something — otherwise marks == trades and
                        // the pair says nothing.
                        label={
                            showWindowTrades && overlayStats?.clustered
                                ? `Trades ${overlayStats.trades.toLocaleString()}·${overlayStats.marks.toLocaleString()}`
                                : 'Trades'
                        }
                        size="small"
                        onClick={props.onToggleWindowTrades}
                        variant={showWindowTrades ? 'filled' : 'outlined'}
                        sx={{
                            height: 20,
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            bgcolor: showWindowTrades
                                ? alpha(theme.palette.info.main, 0.15)
                                : 'transparent',
                            color: showWindowTrades
                                ? theme.palette.info.main
                                : 'text.secondary',
                            borderColor: showWindowTrades
                                ? theme.palette.info.main
                                : 'divider',
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                </Tooltip>
            )}

            {props.convictionControls}

            <Tooltip
                title={
                    props.candlesAllowed
                        ? ''
                        : 'Candlesticks available for a single market'
                }
            >
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={props.effectiveChartType}
                    onChange={(_, v) => {
                        if (!v) return;
                        props.onChartTypeSelect(v);
                    }}
                    sx={toggleGroupSx}
                >
                    <ToggleButton value="line">Line</ToggleButton>
                    <ToggleButton value="candles" disabled={!props.candlesAllowed}>
                        Candles
                    </ToggleButton>
                    {props.stackedAllowed && (
                        <ToggleButton value="stacked">Stacked</ToggleButton>
                    )}
                </ToggleButtonGroup>
            </Tooltip>

            {props.effectiveChartType === 'candles' && (
                <Tooltip title="Which side of the market the candles show">
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={props.candleSide}
                        onChange={(_, v) => {
                            if (!v) return;
                            props.onCandleSideSelect(v);
                        }}
                        sx={toggleGroupSx}
                    >
                        <ToggleButton value="yes">
                            {props.candleSideLabels.yes}
                        </ToggleButton>
                        <ToggleButton value="no">
                            {props.candleSideLabels.no}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Tooltip>
            )}

            <Select
                value={props.resolution}
                onChange={props.onResolutionChange}
                size="small"
                variant="outlined"
                sx={{
                    height: 26,
                    fontSize: '11px',
                    fontWeight: 600,
                    '& .MuiSelect-select': { py: '2px', px: 1 },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                    },
                }}
            >
                {RESOLUTION_OPTIONS.map(opt => (
                    <MenuItem
                        key={opt.value}
                        value={opt.value}
                        sx={{ fontSize: '12px' }}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>

            <ExportButton
                series={props.exportSeries}
                filenamePrefix={props.exportFilenamePrefix}
            />

            <IconButton
                size="small"
                onClick={props.onToggleFullscreen}
                sx={{ p: 0.25, ml: 0.5 }}
            >
                {props.fullscreen ? (
                    <FullscreenExitIcon sx={{ fontSize: 18 }} />
                ) : (
                    <FullscreenIcon sx={{ fontSize: 18 }} />
                )}
            </IconButton>
        </Box>
    );
}
